import React, { useEffect } from 'react';
import stylesSSO from './scss/FormDateFieldSSO.module.scss';
import stylesPP from './scss/FormDateFieldPP.module.scss';
import { BaseFormFieldProps, FormFieldProps } from '../FormField/FormField';
import { useFormContext, useWatch } from 'react-hook-form';
import FormDropdown from '../FormDropdown/FormDropdown';
import {
  PredefinedOptionsList,
  formFieldOptionsList,
} from '../config/form-field-options-lists';
import {
  computeDateAgeYears,
  convertDateToUTC,
  getProjectType,
  isValidDate,
  ProjectTypes,
} from '@r-and-a-shared-ui/utils';
import FormDropdownPP from '../FormDropdownPP/FormDropdownPP';

export interface FormDateFieldProps extends BaseFormFieldProps {
  minimumYear?: number;
  yearOffset?: number;
  computeAge?: boolean;
}

export const FormDateField = (props: FormDateFieldProps) => {
  const {
    name,
    label,
    subfields = [],
    minimumYear,
    yearOffset,
    validationMessage,
    isRequired,
    computeAge,
    hint,
  } = props;

  const {
    setValue,
    formState: { errors },
    control,
    trigger,
  } = useFormContext();

  const watchedValues = useWatch({
    name: subfields?.map(field => field.name),
    control,
  });

  const projectType = getProjectType();
  const styles =
    projectType === ProjectTypes.PlayerPortals ? stylesPP : stylesSSO;

  useEffect(() => {
    const [dayDate, monthDate, yearDate] = watchedValues;
    const isEmpty = !dayDate && !monthDate && !yearDate;
    const isFullDate = dayDate && monthDate && yearDate;

    let date: Date | string | undefined = !isEmpty
      ? `${yearDate}-${monthDate}-${dayDate}`
      : undefined;

    if (isFullDate) {
      const correctMonth = parseInt(monthDate) - 1;
      const isValid = isValidDate(
        parseInt(yearDate),
        correctMonth,
        parseInt(dayDate),
      );
      if (isValid) {
        const localDate = new Date(yearDate, correctMonth, dayDate);
        date = convertDateToUTC(localDate);
      }
    }
    setValue(name, date);

    if (isEmpty && isRequired) {
      return;
    }

    if (computeAge) {
      setValue(`${name}Age`, computeDateAgeYears(date));
    }

    trigger(name);
  }, [watchedValues, setValue, name]);

  const renderDateDropdowns = (
    subfields: FormFieldProps[] | undefined,
  ): React.JSX.Element | undefined => {
    return (
      <div className={styles['form-element__dropdown-container']}>
        {subfields?.map((subFieldProps, index) => {
          const predefinedOptionsListKey = subFieldProps.preferredOptionsList;
          const fieldOptions = formFieldOptionsList[
            predefinedOptionsListKey as PredefinedOptionsList
          ]({
            minimumYear,
            yearOffset,
          });

          const dropdownProps: FormFieldProps = {
            ...subFieldProps,

            options: fieldOptions,
            hideIndividualError: true,
            triggerError: !!errors?.[name],
            menuPlacement: 'top',
            hiddenClearable: isRequired,
          };

          return projectType === ProjectTypes.PlayerPortals ? (
            <FormDropdownPP
              {...dropdownProps}
              key={(subFieldProps.key || index).toString()}
            />
          ) : (
            <FormDropdown
              {...dropdownProps}
              key={(subFieldProps.key || index).toString()}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div id={styles[name]} className={`${styles['form-element__container']}`}>
        <div
          className={`${styles['form-element__title']} ${
            errors?.[name] ? styles['form-element__has-error'] : ''
          }`}
        >
          {label}
        </div>
        {renderDateDropdowns(subfields)}
      </div>
      {errors?.[name] ? (
        <small className={styles['form-element__hint']}>
          {validationMessage}
        </small>
      ) : (
        hint ? (
          <small className={styles['form-element__hint']}>
            {hint}
          </small>
        ) : null
      )}
    </div>
  );
};

export default FormDateField;
